import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { cn } from "@/lib/utils.ts";
import { sanitizeInstructionsInput } from "@/shared/instructions.ts";
import { COMMUNICATIONS_INSTRUCTIONS_MAX_LENGTH } from "@/types/userPreferences.ts";
import { useEffect, useRef, useState } from "react";

type CommunicationsInstructionsProps = {
  communicationsInstructions: string;
};

const PLACEHOLDER = `e.g.
- Sign emails "Cheers, Phegs" instead of "Thanks".
- Keep invitations short — one sentence on purpose, no agenda bullets.
- Always include a phone number in scheduling emails.`;

const SAVE_DEBOUNCE_MS = 800;

// Same defense-in-depth scrub as CustomInstructions.tsx, from shared/client
// so native strips identically.
const sanitizeInput = sanitizeInstructionsInput;

type SaveStatus = "idle" | "saving" | "saved" | "error";

export const CommunicationsInstructions = ({
  communicationsInstructions,
}: CommunicationsInstructionsProps) => {
  const [draft, setDraft] = useState(() => sanitizeInput(communicationsInstructions));
  const [status, setStatus] = useState<SaveStatus>("idle");
  const updateCommunicationsPreferences = useUpdateSurfacePreferences("communications");
  const lastSavedRef = useRef(sanitizeInput(communicationsInstructions).trim());

  const trimmedDraft = draft.trim();
  const remaining = COMMUNICATIONS_INSTRUCTIONS_MAX_LENGTH - trimmedDraft.length;

  useEffect(() => {
    if (trimmedDraft === lastSavedRef.current) return;
    const handle = setTimeout(async () => {
      setStatus("saving");
      try {
        await updateCommunicationsPreferences({ customInstructions: trimmedDraft });
        lastSavedRef.current = trimmedDraft;
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [trimmedDraft, updateCommunicationsPreferences]);

  // Auto-clear "Saved" — see CustomInstructions.tsx for rationale.
  useEffect(() => {
    if (status !== "saved") return;
    const handle = setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 2000);
    return () => clearTimeout(handle);
  }, [status]);

  return (
    <div className="w-full space-y-2">
      <div className="relative">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(sanitizeInput(e.target.value))}
          placeholder={PLACEHOLDER}
          maxLength={COMMUNICATIONS_INSTRUCTIONS_MAX_LENGTH}
          rows={10}
          className="resize-y min-h-[200px] rounded-lg bg-white pb-7 font-mono text-xs"
          data-statsig-name="settings-communications-instructions-textarea"
        />
        <span
          className={cn(
            "pointer-events-none absolute bottom-2 right-3 text-xs text-muted-foreground",
            remaining < 100 && "text-orange-600",
          )}
        >
          {remaining} left
        </span>
      </div>
      <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
        <span>
          Applied to outbound emails and invitations. Angle brackets (&lt;, &gt;) are stripped;
          preferences can't grant new permissions or override safety rules.
        </span>
        <SaveStatusIndicator status={status} />
      </div>
    </div>
  );
};

const SaveStatusIndicator = ({ status }: { status: SaveStatus }) => {
  switch (status) {
    case "saving":
      return <span>Saving…</span>;
    case "saved":
      return <span>Saved</span>;
    case "error":
      return <span className="text-destructive">Save failed</span>;
    default:
      return null;
  }
};
